import React, { useEffect, useMemo } from "react";
import MainLayout from "../../../layouts/MainLayout";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import "../../../assets/styles/custom-styles.css";
import useNotification from "../../../hooks/useNotification";
import useLoader from "../../../hooks/useLoader";
import useForm from "../../../hooks/useForm";
import { setDefaultFieldValues } from "../../../utils/objectUtils";
import { contractDetailsFields } from "../../../constants/forms/contractFields";
import DetailsSectionRenderer from "../../../components/DetailsSectionRenderer";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import ListHeader from "../../../components/ListHeader";
import { usePermissions } from "../../../hooks/usePermissions";
import { transformFiltersForUrl } from "../../../utils/transformFiltersForUrl";
import useGenericFilter from "../../../hooks/filters/useGenericFilter";
import useUrlFilters from "../../../hooks/useUrlFilters";
import useFilterHandlers from "../../../hooks/useFilterHandlers";
import TableWithInactiveFilter from "../../../components/table/TableWithInactiveFilter";
import {
  maskCurrency,
  maskCpfCnpj,
  maskInteger,
} from "../../../utils/maskUtils";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";
import { faRecycle, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useAction from "../../../hooks/useAction";
import ConfirmationModal from "../../../components/modals/ConfirmationModal";

const DetailsContractPage = () => {
  const { contractId } = useParams();
  const { canAccess } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();
  const { showLoader, hideLoader } = useLoader();

  const queryClient = useQueryClient();

  // useForm com formatData
  const { formData, formatData } = useForm(
    setDefaultFieldValues(contractDetailsFields),
  );
  const {
    getByColumn: baseServiceGetByColumn,
    get: baseServiceGet,
    del: baseServiceDelete,
  } = useBaseService(navigate);

  // --- 1. Query para buscar os Detalhes e Infos do Contrato ---
  const {
    data: contractAndInfoData,
    isLoading: isLoadingContractDetails,
    isError: isErrorContractDetails,
    error: contractDetailsError,
  } = useQueryWithLoader({
    queryKey: ["contractDetails", contractId],
    queryFn: async () => {
      const [contractResponse, contractInfosResponse] = await Promise.all([
        baseServiceGetByColumn(entities.contracts.getById(contractId)),
        baseServiceGetByColumn(entities.contracts.infos(contractId)),
      ]);

      return {
        contract: contractResponse.result,
        info: contractInfosResponse.result,
      };
    },
    enabled: !!contractId,
    staleTime: 5 * 60 * 1000,
  });

  // --- useEffect para popular o formData com os detalhes do contrato ---
  useEffect(() => {
    if (contractAndInfoData) {
      formatData(
        {
          contract: contractAndInfoData.contract,
          info: {
            ...contractAndInfoData.info,
            total_amount: maskCurrency(
              Number(contractAndInfoData?.info?.total_amount || 0) +
                Number(contractAndInfoData?.info?.services_total_amount || 0)
            ),
          },
        },
        contractDetailsFields,
      );
    }
  }, [contractAndInfoData, formatData]);

  // --- useEffect para tratar erros da query de detalhes do contrato ---
  useEffect(() => {
    if (isErrorContractDetails && contractDetailsError) {
      console.error(
        "DetailsContractPage: Erro ao carregar detalhes do contrato:",
        contractDetailsError,
      );
      showNotification(
        "error",
        `Erro ao carregar detalhes do contrato: ${
          contractDetailsError.response?.data?.message ||
          contractDetailsError.message
        }`,
      );
      navigate("/aluguel/contratos", {
        replace: true,
        state: {
          type: "error",
          message: "Contrato não encontrado ou erro ao carregar.",
        },
      });
    }
  }, [
    isErrorContractDetails,
    contractDetailsError,
    showNotification,
    navigate,
  ]);

  const {
    filters: filtersRecipients,
    setFilters: applyUrlFiltersRecipients,
    pagination: paginationRecipients,
    changePage: changePageRecipients,
    setPagination: setPaginationRecipients,
  } = useUrlFilters(
    {
      deleted_at: false,
    },
    "contractRecipients",
  );

  const {
    handleFilterApply: handleFilterApplyRecipients,
    handleFilterClear: handleFilterClearRecipients,
  } = useFilterHandlers(applyUrlFiltersRecipients);

  const { localFilterValues: localFilterValuesRecipients } = useGenericFilter(
    {
      deleted_at: false,
    },
    handleFilterApplyRecipients,
    handleFilterClearRecipients,
  );

  // --- 2. Query para buscar Itens do Contrato ---
  const {
    data: contractRecipientsData,
    isLoading: isLoadingRecipients,
    isFetching: isFetchingRecipients,
  } = useQueryWithLoader({
    queryKey: [
      "contractRecipients",
      contractId,
      filtersRecipients,
      paginationRecipients,
    ],
    queryFn: async () => {
      const filtersTranformed = transformFiltersForUrl({
        deleted_at: false,
      });
      const params = {
        filters: filtersTranformed,
        page: paginationRecipients.page,
        perPage: paginationRecipients.perPage,
      };
      const response = await baseServiceGet(
        entities.contracts.contractRecipients(contractId),
        params,
      );
      return response.result;
    },
    enabled: !!contractId,
    staleTime: 60 * 1000,
    select: (data) => ({
      recipients: data.data.map((item) => ({
        id: item.id,
        alias: item.alias,
        name: item.name,
        cpf_cnpj: maskCpfCnpj(item.cpf_cnpj),
        isDeleted: !!item.deleted_at,
      })),
      totalPages: data.totalPages,
    }),
  });

  const columnsRecipients = [
    { key: "alias", label: "Cliente Destinatário" },
    { key: "name", label: "Nome" },
    { key: "cpf_cnpj", label: "CPF/CNPJ" },
  ];

  const { recipients = [], totalPages: totalPagesRecipients = 1 } =
    contractRecipientsData || [];

  const {
    filters: filtersItems,
    setFilters: applyUrlFiltersItems,
    pagination: paginationItems,
    changePage: changePageItems,
    setPagination: setPaginationItems,
  } = useUrlFilters(
    {
      deleted_at: false,
    },
    "contractItems",
  );

  const {
    handleFilterApply: handleFilterApplyItems,
    handleFilterClear: handleFilterClearItems,
  } = useFilterHandlers(applyUrlFiltersItems);

  const { localFilterValues: localFilterValuesItems } = useGenericFilter(
    {
      deleted_at: false,
    },
    handleFilterApplyItems,
    handleFilterClearItems,
  );

  // --- 2. Query para buscar Itens do Contrato ---
  const {
    data: contractItemsData,
    isLoading: isLoadingContractItems,
    isFetching: isFetchingContractItems,
    isError: isErrorContractItems,
    error: contractItemsError,
  } = useQueryWithLoader({
    queryKey: ["contractItems", contractId, filtersItems, paginationItems],
    queryFn: async () => {
      const filtersTranformed = transformFiltersForUrl(filtersItems);
      const params = {
        filters: filtersTranformed,
        page: paginationItems.page,
        perPage: paginationItems.perPage,
      };
      const response = await baseServiceGet(
        entities.contracts.items(contractId),
        params,
      );
      return response.result;
    },
    enabled: !!contractId,
    staleTime: 60 * 1000,
    select: (data) => ({
      items: data.data.map((item) => {
        const contratado = Number(item.quantity) || 0;

        // Saldo real: `available_quantity` é o campo do contrato que é
        // debitado a cada requisição e devolvido quando o equipamento
        // retorna (devolução), que é a regra do saldo.
        const saldo =
          item.available_quantity != null
            ? Number(item.available_quantity)
            : contratado;
        const requisitado = Math.max(0, contratado - saldo);

        return {
          id: item.id,
          alias: item.alias,
          description: item.description,
          quantity: maskInteger(contratado),
          requisitado: maskInteger(requisitado),
          saldo: maskInteger(saldo),
          saldoValue: saldo,
          available_quantity: item.available_quantity,
          event_additive_id: item.event_additive_id,
          price: item.price,
          contract_total: contratado * (Number(item.price) || 0),
          isDeleted: !!item.deleted_at,
        };
      }),
      totalPages: data.totalPages,
    }),
  });

  const columnsItems = [
    { key: "alias", label: "Nome do item" },
    { key: "description", label: "Descrição" },
    { key: "quantity", label: "Contratado" },
    { key: "requisitado", label: "Requisitado" },
    {
      key: "saldo",
      label: "Saldo",
      render: (value, row) => (
        <span
          style={{
            fontWeight: 700,
            color: Number(row.saldoValue) <= 0 ? "#c62828" : "#2e7d32",
          }}
        >
          {value}
        </span>
      ),
    },
    {
      key: "price",
      label: "Preço",
      isMoney: true,
    },
    { key: "contract_total", label: "Valor total contratado", isMoney: true },
  ];

  const { items = [], totalPages: totalPagesItems = 1 } =
    contractItemsData || [];

  // --- useEffect para tratar erros da query de itens do contrato ---
  useEffect(() => {
    if (isErrorContractItems && contractItemsError) {
      console.error(
        "DetailsContractPage: Erro ao carregar itens do contrato:",
        contractItemsError,
      );
      showNotification(
        "error",
        `Erro ao carregar itens do contrato: ${
          contractItemsError.response?.data?.message ||
          contractItemsError.message
        }`,
      );
    }
  }, [isErrorContractItems, contractItemsError, showNotification]);

  const {
    filters: filtersJobs,
    setFilters: applyUrlFiltersJobs,
    pagination: paginationJobs,
    changePage: changePageJobs,
    setPagination: setPaginationJobs,
  } = useUrlFilters(
    {
      deleted_at: false,
    },
    "contractJobs",
  );

  const {
    handleFilterApply: handleFilterApplyJobs,
    handleFilterClear: handleFilterClearJobs,
  } = useFilterHandlers(applyUrlFiltersJobs);

  const { localFilterValues: localFilterValuesJobs } = useGenericFilter(
    {
      deleted_at: false,
    },
    handleFilterApplyJobs,
    handleFilterClearJobs,
  );

  // --- 3. Query para buscar Serviços do Contrato ---
  const {
    data: contractJobsData,
    isLoading: isLoadingContractJobs,
    isFetching: isFetchingContractJobs,
    isError: isErrorContractJobs,
    error: contractJobsError,
  } = useQueryWithLoader({
    queryKey: ["contractJobs", contractId, filtersJobs, paginationJobs],
    queryFn: async () => {
      const filtersTranformed = transformFiltersForUrl(filtersJobs);
      const params = {
        filters: filtersTranformed,
        page: paginationJobs.page,
        perPage: paginationJobs.perPage,
      };
      const response = await baseServiceGet(
        entities.contracts.jobs(contractId),
        params,
      );
      return response.result;
    },
    enabled: !!contractId,
    staleTime: 60 * 1000,
    select: (data) => ({
      jobs: data.data.map((job) => ({
        id: job.id,
        alias: job.alias,
        description: job.description,
        quantity: maskInteger(job.quantity),
        attended_quantity: `${maskInteger(job.attended_quantity || 0)} / ${maskInteger(job.quantity)}`,
        event_additive_id: job.event_additive_id,
        price: job.price,
        isDeleted: !!job.deleted_at,
      })),
      totalPages: data.totalPages,
    }),
  });

  const columnsJobs = [
    { key: "alias", label: "Nome" },
    { key: "description", label: "Descrição" },
    { key: "quantity", label: "Quantidade contratada" },
    { key: "attended_quantity", label: "Progresso (Faturado)" },
    { key: "price", label: "Preço", isMoney: true },
  ];

  const { jobs = [], totalPages: totalPagesJobs = 1 } = contractJobsData || [];

  // --- useEffect para tratar erros da query de serviços do contrato ---
  useEffect(() => {
    if (isErrorContractJobs && contractJobsError) {
      console.error(
        "DetailsContractPage: Erro ao carregar serviços do contrato:",
        contractJobsError,
      );
      showNotification(
        "error",
        `Erro ao carregar serviços do contrato: ${
          contractJobsError.response?.data?.message || contractJobsError.message
        }`,
      );
    }
  }, [isErrorContractJobs, contractJobsError, showNotification]);

  // --- Gerenciamento do Loader Global (consolidado) ---
  useEffect(() => {
    const isAnyLoadingOrFetching =
      isLoadingContractDetails ||
      isLoadingContractItems ||
      isLoadingContractJobs;
    if (isAnyLoadingOrFetching) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [
    isLoadingContractDetails,
    isLoadingContractItems,
    isLoadingContractJobs,
    showLoader,
    hideLoader,
  ]);

  // --- useEffect para mensagens de estado de localização (PADRÃO) ---
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  // Hook useAction para gerenciar ações de exclusão/ativação com modal de confirmação
  const eventId = contractAndInfoData?.contract?.current_event?.id;

  const {
    openModalConfirmation,
    action,
    showConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
  } = useAction(navigate);

  const inactiveContractItemMutation = useMutation({
    mutationFn: async ({ event_additive_id, itemId }) => {
      showLoader();
      const response = await baseServiceDelete(
        entities.contracts.eventAdditiveItemDelete(
          contractId,
          eventId,
          event_additive_id,
          itemId,
        ),
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        hideLoader();
        queryClient.invalidateQueries({ queryKey: ["contractItems"] });
      }
    },
    onError: (err) => {
      console.error("onError ", err);
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
      handleCancelConfirmation(); // Fecha o modal de confirmação
    },
  });

  const restoreContractItemMutation = inactiveContractItemMutation;

  const actionsItem = useMemo(
    () => [
      {
        id: "delete",
        icon: faTrash,
        title: "Excluir",
        buttonClass: "btn-danger",
        permission: "Excluir itens de contrato",
        onClick: (contractItem) => {
          showConfirmation(
            contractItem,
            `Você tem certeza que deseja excluir?`,
            async () => {
              try {
                await inactiveContractItemMutation.mutateAsync({
                  event_additive_id: contractItem.event_additive_id,
                  itemId: contractItem.id,
                });
              } catch (error) {
                console.error("Erro ao excluir item do contrato:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          );
        },
        show: (contractItem) =>
          !contractItem.isDeleted &&
          contractItem.available_quantity === contractItem.quantity &&
          canAccess("Excluir itens de contratos"),
      },
      {
        id: "restore",
        icon: faRecycle,
        title: "Restaurar",
        buttonClass: "btn-success",
        permission: "Excluir itens de contratos",
        onClick: (contractItem) => {
          showConfirmation(
            contractItem,
            `Você tem certeza que deseja restaurar?`,
            async () => {
              try {
                await restoreContractItemMutation.mutateAsync({
                  event_additive_id: contractItem.event_additive_id,
                  itemId: contractItem.id,
                });
              } catch (error) {
                console.error("Erro ao excluir item do contrato:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          );
        },
        show: (contractItem) =>
          contractItem.isDeleted &&
          contractItem.available_quantity === contractItem.quantity &&
          canAccess("Excluir itens de contratos"),
      },
    ],
    [
      showConfirmation,
      inactiveContractItemMutation,
      canAccess,
      restoreContractItemMutation,
    ],
  );

  const inactiveContractJobMutation = useMutation({
    mutationFn: async ({ event_additive_id, jobId }) => {
      showLoader();
      const response = await baseServiceDelete(
        entities.contracts.eventAdditiveJobDelete(
          contractId,
          eventId,
          event_additive_id,
          jobId,
        ),
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        hideLoader();
        queryClient.invalidateQueries({ queryKey: ["contractJobs"] });
      }
    },
    onError: (err) => {
      console.error("onError ", err);
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
      handleCancelConfirmation(); // Fecha o modal de confirmação
    },
  });

  const restoreContractJobMutation = inactiveContractJobMutation;

  const actionsJob = useMemo(
    () => [
      {
        id: "delete",
        icon: faTrash,
        title: "Excluir",
        buttonClass: "btn-danger",
        permission: "Excluir serviços de contratos",
        onClick: (contractJob) => {
          showConfirmation(
            contractJob,
            `Você tem certeza que deseja excluir?`,
            async () => {
              try {
                await inactiveContractJobMutation.mutateAsync({
                  event_additive_id: contractJob.event_additive_id,
                  jobId: contractJob.id,
                });
              } catch (error) {
                console.error("Erro ao excluir item do contrato:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          );
        },
        show: (contractJob) =>
          !contractJob.isDeleted && canAccess("Excluir serviços de contratos"),
      },
      {
        id: "restore",
        icon: faRecycle,
        title: "Restaurar",
        buttonClass: "btn-success",
        permission: "Excluir serviços de contratos",
        onClick: (contractJob) => {
          showConfirmation(
            contractJob,
            `Você tem certeza que deseja restaurar?`,
            async () => {
              try {
                await restoreContractJobMutation.mutateAsync({
                  event_additive_id: contractJob.event_additive_id,
                  jobId: contractJob.id,
                });
              } catch (error) {
                console.error("Erro ao excluir item do contrato:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          );
        },
        show: (contractJob) =>
          contractJob.isDeleted && canAccess("Excluir serviços de contratos"),
      },
    ],
    [
      showConfirmation,
      inactiveContractJobMutation,
      canAccess,
      restoreContractJobMutation,
    ],
  );

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title={`Detalhes do Contrato`}
        showBackButton={true}
        backUrl="/aluguel/contratos"
      />
      <div className="container-fluid p-1">
        <DetailsSectionRenderer
          formData={formData}
          sections={contractDetailsFields}
        />

        <ListHeader
          title="Destinatários do Contrato"
          buttonIsNotVisible={true}
          canAccess={canAccess}
        />

        <TableWithInactiveFilter
          data={recipients || []}
          columns={columnsRecipients}
          actions={[]}
          showFilterInactive={false}
          totalPages={totalPagesRecipients}
          currentPage={paginationRecipients.page - 1}
          onPageChange={changePageRecipients}
          rowsPerPage={paginationRecipients.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value;
            setPaginationRecipients({ page: 1, perPage: newPerPage });
          }}
          filterValues={localFilterValuesRecipients}
          localFilterValues={localFilterValuesRecipients}
          applyUrlFilters={applyUrlFiltersRecipients}
          type="default"
          isLoading={isLoadingRecipients}
          isFetching={isFetchingRecipients}
        />

        <ListHeader
          title="Itens"
          buttonIsNotVisible={true}
          canAccess={canAccess}
        />
        <TableWithInactiveFilter
          data={items || []}
          columns={columnsItems}
          actions={actionsItem}
          showFilterInactive={false}
          totalPages={totalPagesItems}
          currentPage={paginationItems.page - 1}
          onPageChange={changePageItems}
          rowsPerPage={paginationItems.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value;
            setPaginationItems({ page: 1, perPage: newPerPage });
          }}
          filterValues={localFilterValuesItems}
          localFilterValues={localFilterValuesItems}
          applyUrlFilters={applyUrlFiltersItems}
          type="default"
          isLoading={isLoadingContractItems}
          isFetching={isFetchingContractItems}
        />

        <ListHeader
          title="Serviços"
          buttonIsNotVisible={true}
          canAccess={canAccess}
        />
        <TableWithInactiveFilter
          data={jobs || []}
          columns={columnsJobs}
          actions={actionsJob}
          showFilterInactive={false}
          totalPages={totalPagesJobs}
          currentPage={paginationJobs.page - 1}
          onPageChange={changePageJobs}
          rowsPerPage={paginationJobs.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value;
            setPaginationJobs({ page: 1, perPage: newPerPage });
          }}
          filterValues={localFilterValuesJobs}
          localFilterValues={localFilterValuesJobs}
          applyUrlFilters={applyUrlFiltersJobs}
          type="default"
          isLoading={isLoadingContractJobs}
          isFetching={isFetchingContractJobs}
        />

        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={selectedItem?.name ?? ""}
          text={action.text}
        />
      </div>
    </MainLayout>
  );
};

export default DetailsContractPage;
